import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    "Content-Type": "application/json",
  },
});

// Send the login token with every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Expired or invalid login: clear it and go back to the login page
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLoginRequest = error.config?.url?.includes("/auth/login");
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      if (window.location.pathname !== "/login") {
        window.location.assign("/login");
      }
    }
    return Promise.reject(error);
  }
);

// Dashboard
export const getDashboardStats = () => api.get("/dashboard/stats");

// Websites
export const analyzeWebsite = (url, niche) => api.post("/websites/analyze", { url, niche });
export const bulkAnalyzeWebsites = (urls, niche) => api.post("/websites/bulk-analyze", { urls, niche });
export const discoverWebsites = (industry, maxResults = 20, forceRefresh = false, checks = {}) => api.post("/websites/discover", { industry, max_results: maxResults, force_refresh: forceRefresh, checks });
export const getDiscoveryRun = (runId) => api.get(`/websites/discover/runs/${runId}`);
export const getDiscoveryRuns = (limit = 10) => api.get("/websites/discover/runs", { params: { limit } });
export const getDiscoveryHistory = () => api.get("/websites/discover/history");
export const getWebsites = (params) => api.get("/websites", { params });
export const getWebsite = (id) => api.get(`/websites/${id}`);
export const deleteWebsite = (id) => api.delete(`/websites/${id}`);
export const refreshWebsiteEmail = (id) => api.post(`/websites/${id}/refresh-email`);
export const getQualificationStats = () => api.get("/websites/stats/qualification");

// Contact Lists
export const createContactList = (data) => api.post("/contact-lists", data);
export const getContactLists = () => api.get("/contact-lists");
export const getContactList = (id) => api.get(`/contact-lists/${id}`);
export const getContactListWebsites = (id) => api.get(`/contact-lists/${id}/websites`);
export const addWebsitesToList = (id, websiteIds) => api.put(`/contact-lists/${id}/add-websites`, websiteIds);
export const deleteContactList = (id) => api.delete(`/contact-lists/${id}`);
export const verifyContactList = (id) => api.post(`/contact-lists/${id}/verify`);

// Templates
export const createTemplate = (data) => api.post("/templates", data);
export const getTemplates = (type) => api.get("/templates", { params: { template_type: type } });
export const getTemplate = (id) => api.get(`/templates/${id}`);
export const deleteTemplate = (id) => api.delete(`/templates/${id}`);
export const generateAITemplate = (data) => api.post("/templates/generate-ai", data);

// Campaigns
export const createCampaign = (data) => api.post("/campaigns", data);
export const getCampaigns = (status) => api.get("/campaigns", { params: { status } });
export const getCampaign = (id) => api.get(`/campaigns/${id}`);
export const updateCampaign = (id, data) => api.put(`/campaigns/${id}`, data);
export const deleteCampaign = (id) => api.delete(`/campaigns/${id}`);
export const launchCampaign = (id) => api.post(`/campaigns/${id}/launch`);

// Emails
export const getEmailLogs = (params) => api.get("/emails", { params });
export const sendEmail = (campaignId, websiteId) => api.post("/emails/send", { campaign_id: campaignId, website_id: websiteId });
export const markEmailReplied = (id) => api.post(`/emails/${id}/mark-replied`);

// Settings
export const getSettings = () => api.get("/settings");
export const updateSettings = (data) => api.put("/settings", data);

// Keyword Research
export const previewKeywordExpansion = (data) => api.post("/keywords/preview", data);
export const startKeywordResearch = (data) => api.post("/keywords/research", data);
export const getKeywordJobs = (limit = 20) => api.get("/keywords/jobs", { params: { limit } });
export const getKeywordJob = (jobId) => api.get(`/keywords/jobs/${jobId}`);
export const exportKeywordResults = (jobId, format = "json") => api.get(`/keywords/jobs/${jobId}/export`, { params: { format } });
export const getExpansionOptions = () => api.get("/keywords/expansion-options");

// Batch Keyword Research (Optimized)
export const previewBatchKeywords = (batches) => api.post("/keywords/batch/preview", { batches });
export const runBatchKeywordResearch = (batches) => api.post("/keywords/batch/research", { batches });
export const getBatchKeywordJobs = (limit = 20) => api.get("/keywords/batch/jobs", { params: { limit } });
export const getBatchKeywordJob = (jobId) => api.get(`/keywords/batch/jobs/${jobId}`);
export const exportBatchKeywordResults = (jobId) => api.get(`/keywords/batch/jobs/${jobId}/export?format=csv`);

// Smart Keyword Suggestions
export const getKeywordSuggestions = (keyword, limit = 10) => api.post("/keywords/suggestions", { keyword, limit });

export default api;
